import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { formatLocationCoords } from '../lib/format.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';

interface ReceiptPallet {
  id: string;
  code: string;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  contents: {
    skuId: string;
    remainingQty: number;
    sku: { code: string; nameLt: string; nameEn: string | null; type: 'PRODUCT' | 'COMPONENT' };
  }[];
}
const { t } = i18n;

export function ReceiptPalletPicker({
  partnerId,
  value,
  onChange,
}: {
  partnerId: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const { user } = useAuth();
  const [pallets, setPallets] = useState<ReceiptPallet[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    api<ReceiptPallet[]>(`/partners/${partnerId}/stock/receipt-pallets`, {
      signal: controller.signal,
    })
      .then((rows) => {
        if (!controller.signal.aborted) setPallets(rows);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [partnerId]);
  if (failed) return <div role="alert">{t('common.error')}</div>;
  if (!pallets) return <div role="status">{t('common.loading')}</div>;
  const query = search.trim().toLocaleLowerCase();
  const rows = pallets.filter((pallet) =>
    `${pallet.code} ${formatLocationCoords(pallet) ?? ''} ${pallet.contents.map((line) => `${line.sku.code} ${line.sku.nameLt} ${line.sku.nameEn ?? ''}`).join(' ')}`
      .toLocaleLowerCase()
      .includes(query),
  );
  return (
    <fieldset className="space-y-2">
      <legend className="font-medium">{t('stock.restock.pallet_list')}</legend>
      <p className="text-xs text-muted-foreground">{t('stock.restock.pallet_hint')}</p>
      <input
        type="search"
        aria-label={t('stock.restock.pallet_search')}
        placeholder={t('stock.restock.pallet_search')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="w-full rounded border border-border bg-background px-2 py-1"
      />
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {rows.length === 0 ? (
          <p>{t('stock.restock.pallet_empty')}</p>
        ) : (
          rows.map((pallet) => (
            <label
              key={pallet.id}
              className={`block cursor-pointer rounded border p-2 ${value === pallet.id ? 'border-primary bg-primary/5' : 'border-border'}`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="receipt-pallet"
                  value={pallet.id}
                  checked={value === pallet.id}
                  onChange={() => onChange(pallet.id)}
                />
                <span className="font-medium">{pallet.code}</span>
                <span className="text-xs text-muted-foreground">
                  {formatLocationCoords(pallet)}
                </span>
              </span>
              {pallet.contents.length === 0 ? (
                <p className="ml-6 text-xs text-muted-foreground">
                  {t('pallet.contents.preview_empty')}
                </p>
              ) : (
                <ul className="ml-6 mt-1 space-y-1 text-xs">
                  {pallet.contents.map((line) => (
                    <li key={line.skuId} className="flex gap-2">
                      <span className="min-w-0 flex-1">
                        {line.sku.code} —{' '}
                        {user?.locale === 'en'
                          ? line.sku.nameEn || line.sku.nameLt
                          : line.sku.nameLt}{' '}
                        ({t(`sku.type.${line.sku.type}`)})
                      </span>
                      <span className="tabular-nums">{line.remainingQty}</span>
                    </li>
                  ))}
                </ul>
              )}
            </label>
          ))
        )}
      </div>
    </fieldset>
  );
}
